import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ThemeProvider } from '@mui/material';
import { buildTheme, DEFAULT_THEME_ID, isThemeId, themeBackground, type ThemeId } from './theme';

/**
 * The selected colour theme.
 *
 * Held here once so components read `useThemeId()` rather than each deciding
 * the theme for themselves. Persisted per browser in localStorage.
 *
 * STORAGE_KEY is also read by the inline script in index.html, which paints the
 * saved background before the bundle loads. Change both together.
 */

const STORAGE_KEY = 'silverline.theme';

interface ThemeContextValue {
    readonly themeId: ThemeId;
    readonly setThemeId: (id: ThemeId) => void;
}

const ThemeIdContext = createContext<ThemeContextValue | null>(null);

function readStored(): ThemeId {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        return isThemeId(v) ? v : DEFAULT_THEME_ID;
    } catch {
        // Storage can throw in private windows or with site data blocked.
        return DEFAULT_THEME_ID;
    }
}

export function AppThemeProvider({ children }: { children: ReactNode }): React.JSX.Element {
    const [themeId, setThemeIdState] = useState<ThemeId>(readStored);
    const theme = useMemo(() => buildTheme(themeId), [themeId]);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, themeId);
        } catch {
            // Not persisting is acceptable; the choice still applies this session.
        }
        const bg = themeBackground(themeId);
        document.documentElement.style.backgroundColor = bg;
        document.querySelector('meta[name="theme-color"]')?.setAttribute('content', bg);
    }, [themeId]);

    const value = useMemo(() => ({ themeId, setThemeId: setThemeIdState }), [themeId]);

    return (
        <ThemeIdContext.Provider value={value}>
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </ThemeIdContext.Provider>
    );
}

export function useThemeId(): ThemeContextValue {
    const ctx = useContext(ThemeIdContext);
    if (ctx === null) {
        throw new Error('useThemeId must be used inside AppThemeProvider');
    }
    return ctx;
}
